/* eslint-disable max-lines */
/* eslint-disable react-func/max-lines-per-function */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import lookie from 'lookie';
import { Button } from 'antd-mobile';
import { Icon } from '@fuxi/eevee-icon';
import { Decimal } from 'decimal.js';
import { convertObjectKeysToCamelCase } from 'common-package';
import { useLatest, useLockFn } from 'ahooks';
import { klona } from 'klona';
import { nanoid } from '@reduxjs/toolkit';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrentServingOrder, setCurrentServingOrder } from '@/store/service';
import { CheckArm, Header, Model2D, Stepper, SlideSelect, ServiceProcessCrumb, HeaderMemberInfo } from '@/components';
import { useChassisStatePrompt, useNav } from '@/hooks';
import { genMinMaxSteps } from '@/components/stepper';
import { CheckArmRefAttrs } from '@/components/check-arm';
import { MatrixMoxibustionArea, Model2DRefAttrs } from '@/components/model-2d';
import { ALL_PAGES, BODY_PART, MassageType2Text, SELECTED_CUSTOMER_ID, UPPER_STATUS_CODE } from '@/constant';
import service from '@/service';
import { handleNavFromOrderInfo, Toast } from '@/utils';
import UserGuideTips from '../massage-setting/user-guide-tips';

import cx from './index.module.scss';

interface Props {}

const controlItemName2Label: Partial<
  Record<
    Massage.ControlItemOptions,
    {
      label: string;
      suffix?: React.ReactNode;
    }
  >
> = {
  'moxibustion.acupoint_area': {
    label: '穴区',
  },
  'moxibustion.duration': {
    label: '时长',
    suffix: '分钟',
  },
  'moxibustion.tech': {
    label: '灸法',
  },
  'robot.height': {
    label: '距离人体表面高度',
    suffix: 'cm',
  },
};

type SettingValues = {
  'moxibustion.acupoint_area': number;
  'moxibustion.duration': number;
  'moxibustion.tech': number;
  // 'robot.height': number;
} & { addId: string };

// 按摩设置-矩阵式艾灸
const MassageSetting: React.FC<Props> = () => {
  const model2dRef = useRef<Model2DRefAttrs>(null);
  const nav = useNav();
  const dispatch = useDispatch();
  const currentServingOrder = useSelector(selectCurrentServingOrder);
  const [massageSettingsOptions, setMassageSettingsOptions] = useState<GetMassageSettingsOptionsResponse>(null);
  const [isConfirmingPlan, setIsConfirmingPlan] = useState(false);
  const checkArmRef = useRef<CheckArmRefAttrs>(null);

  const [robotHeight, setRobotHeight] = useState(100);
  const [currentSelectConfigIndex, setCurrentSelectConfigIndex] = useState(-1);
  const [moxConfigList, setMoxConfigList] = useState<SettingValues[]>([]);
  const latestOrder = useLatest(currentServingOrder);
  const [productInfo, setProductInfo] = useState<GetProductResponse>(null);
  const [isRenderModelOK, setIsRenderModelOK] = useState(false);

  // 根据穴区ID排序
  const sortMoxConfigListByAcupoint = useMemo(
    () =>
      klona(moxConfigList)?.sort((a, b) => a?.['moxibustion.acupoint_area'] - b?.['moxibustion.acupoint_area']) || [],
    [moxConfigList]
  );

  // 当前单个穴区允许配置的最大时长
  const ALLOW_SINGLE_MAX_TIME = useMemo(
    () =>
      +massageSettingsOptions?.[0]?.controlItemConfigs
        ?.find(item => item.name === 'moxibustion.duration')
        ?.values?.slice(-1)[0]?.value || 60,
    [massageSettingsOptions]
  );

  const modelSolution: Massage.ModelSolution = useMemo(() => {
    // return 'matrix-moxibustion';
    if (currentServingOrder?.productItems?.includes('Shockwave')) {
      return currentServingOrder?.modelConfig?.find(item => item.key === 'Shockwave')?.value;
    } else {
      return currentServingOrder?.modelConfig?.[0]?.value;
    }
  }, [currentServingOrder?.modelConfig, currentServingOrder?.productItems]);

  const { loopGetDeviceChassisState } = useChassisStatePrompt();

  const initData = useCallback(async () => {
    try {
      const { data: servingOrder } = await service.service.getServingOrderByOperator();
      if (!servingOrder?.orderId) {
        Toast.show('当前任务已结束');
        nav(ALL_PAGES.MEMBER_DETAIL);
        return;
      }

      const { data: productInfo } = await service.service.getProduct(servingOrder?.storeGoodsId);

      setProductInfo(productInfo);

      dispatch(setCurrentServingOrder(servingOrder));
      lookie.set(SELECTED_CUSTOMER_ID, servingOrder?.customerId);
      // 通过接口数据设置默认值
      const { data: options } = await service.massage.getMassageSettingsOptions(servingOrder?.orderId);
      setMassageSettingsOptions(convertObjectKeysToCamelCase(options));

      const { data } = await service.massage.getMassageUserSettings(servingOrder?.orderId);
      if (!data?.moxibustionSettings) {
        return;
      }

      // 回显之前设置的矩阵式艾灸参数
      const prevMoxConfigs = JSON.parse(data.moxibustionSettings)?.[0];
      if (!prevMoxConfigs) return;

      setMoxConfigList(
        prevMoxConfigs?.acupoint_areas?.map((item, index) => ({
          'moxibustion.acupoint_area': item?.['acupoint_area'],
          'moxibustion.duration': item?.['duration'],
          'moxibustion.tech': item?.['tech'],
          addId: String(index),
        }))
      );

      setRobotHeight(prevMoxConfigs?.height);
      setCurrentSelectConfigIndex(0);
    } finally {
    }
  }, [dispatch, nav]);

  // 确认并开始
  const handleConfirmAndStart = useCallback(async () => {
    const curentProduct = currentServingOrder?.productItems?.[0];

    if (curentProduct !== 'Moxibustion') return;

    try {
      const acupoint_areas = moxConfigList.map(item => ({
        acupoint_area: item['moxibustion.acupoint_area'],
        tech: item['moxibustion.tech'],
        duration: item['moxibustion.duration'],
      }));

      await service.massage.startRoboticTask({
        serviceOrderId: currentServingOrder.orderId,
        moxibustionSettings: JSON.stringify([
          {
            acupoint_areas,
            height: robotHeight,
          },
        ]),
      });

      const { data } = await service.service.getServingOrderByOperator();
      dispatch(setCurrentServingOrder(data));
      handleNavFromOrderInfo(data, nav);
    } catch (e) {
      console.error(e);
      setIsConfirmingPlan(false);
      if (e?.code === UPPER_STATUS_CODE.ChassisLockingOrUnLocking) {
        loopGetDeviceChassisState(currentServingOrder?.deviceId);
      } else {
        e?.message && Toast.show(e?.message);
      }
    }
  }, [currentServingOrder, dispatch, loopGetDeviceChassisState, moxConfigList, nav, robotHeight]);

  // 当前允许添加的最大时长
  const currentAllowMaxMinutes = useMemo(
    () => 60 - moxConfigList.reduce((accur, cur) => accur + +cur['moxibustion.duration'], 0),
    [moxConfigList]
  );

  // 当前可配置的最大时长
  const currentMaxDuration = useMemo(
    () =>
      Math.min(
        ALLOW_SINGLE_MAX_TIME,
        60 -
          moxConfigList
            .filter((_, index) => index !== currentSelectConfigIndex)
            .reduce((accur, cur) => accur + +cur['moxibustion.duration'], 0)
      ),
    [ALLOW_SINGLE_MAX_TIME, currentSelectConfigIndex, moxConfigList]
  );

  // 添加穴区
  const handleAddMatrixMoxArea = useCallback(() => {
    if (!isRenderModelOK) return;

    setCurrentSelectConfigIndex(moxConfigList?.length);

    setMoxConfigList(prevValue => {
      const backupValue = klona(prevValue);

      const defaultSettingOptions: SettingValues = {
        'moxibustion.acupoint_area': +massageSettingsOptions[0]?.controlItemConfigs?.find(
          item => item.name === 'moxibustion.acupoint_area'
        )?.default?.value,
        'moxibustion.tech': +massageSettingsOptions[0]?.controlItemConfigs?.find(
          item => item.name === 'moxibustion.tech'
        )?.default?.value,
        'moxibustion.duration': Math.min(
          currentAllowMaxMinutes,
          +massageSettingsOptions[0]?.controlItemConfigs?.find(item => item.name === 'moxibustion.duration')?.default
            ?.value || 30
        ),
        addId: nanoid(6),
      };

      backupValue.push(defaultSettingOptions);
      return backupValue;
    });
  }, [currentAllowMaxMinutes, isRenderModelOK, massageSettingsOptions, moxConfigList?.length]);

  const handleCheckArm = useLockFn(
    useCallback(async () => {
      setIsConfirmingPlan(true);

      const { data: order } = await service.service.getServingOrderByOperator();
      if (!order) {
        Toast.show('检测到长时间未操作，已为您自动结束订单');
        nav(ALL_PAGES.MEMBER_DETAIL);
        return;
      }

      try {
        checkArmRef.current?.setMassageProcess('order-before');
        await checkArmRef.current?.handleCheckArm(currentServingOrder?.device);
      } catch (e) {
        e?.error !== 'Visual' && setIsConfirmingPlan(false);
      }
    }, [currentServingOrder?.device, nav])
  );

  useEffect(() => {
    initData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 渲染模型
  useEffect(() => {
    if (isRenderModelOK) {
      const currentSelectArea = moxConfigList.map(item => ({
        name: `image${item['moxibustion.acupoint_area']}`,
      })) as MatrixMoxibustionArea[];

      model2dRef.current.setMatrixMoxibustionArea(currentSelectArea, {
        isAbdomen: latestOrder.current?.bodyPart === 'Abdomen',
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRenderModelOK, moxConfigList]);

  return (
    <div className={cx('massage-setting')}>
      <Header
        title={
          <HeaderMemberInfo
            name={`${currentServingOrder?.customerName}`}
            handleClick={() => {}}
            avatarUrl={currentServingOrder?.avatarUrl ?? currentServingOrder?.userAvatar}
          />
        }
        backConfig={{ text: '会员详情', action: () => nav(ALL_PAGES.MEMBER_DETAIL) }}
      />
      <UserGuideTips />
      <CheckArm onCheckArmPass={handleConfirmAndStart} ref={checkArmRef} />
      <ServiceProcessCrumb className={cx('crumb')} currentPageStep="MassageSetting" />
      <div className={cx('view-wrapper')}>
        <div className={cx('left')}>
          <div className={cx('title')}>
            <div className={cx('name')}>{currentServingOrder?.productName}</div>
            <div className={cx('massage-type')}>
              <span className={cx('massage-type-text')}>
                {MassageType2Text[currentServingOrder?.productItems?.[0]]}
              </span>
            </div>
          </div>
          <div className={cx('model-wrapper')}>
            <Model2D
              ref={model2dRef}
              isMatrixMox={true}
              modelSolution={modelSolution}
              onRenderModelOK={() => setTimeout(() => setIsRenderModelOK(true), 300)}
              bodyNodeList={[]}
            />
            <div className={cx('config-list')}>
              {currentServingOrder?.bodyPart === 'Abdomen' ? (
                <div className={cx('abdomen-box')}>
                  {new Array(2).fill('').map((_, outerIndex) => (
                    <div key={nanoid(5)} className={cx('acupoint-item', `acupoint-item-${outerIndex + 6}`)}>
                      {moxConfigList.map(
                        (item, index) =>
                          +item['moxibustion.acupoint_area'] === outerIndex + 6 && (
                            <div
                              key={nanoid()}
                              className={cx('config-item', { current: currentSelectConfigIndex === index })}
                              onClick={() => setCurrentSelectConfigIndex(index)}
                            >
                              <div className={cx('index')}>
                                {sortMoxConfigListByAcupoint?.findIndex(item2 => item2.addId === item.addId) + 1}.
                              </div>
                              <div className={cx('acu-area')}>
                                {
                                  massageSettingsOptions?.[0].controlItemConfigs
                                    ?.find(item2 => item2.name === 'moxibustion.acupoint_area')
                                    ?.values?.find(item3 => +item3.value === item['moxibustion.acupoint_area'])?.label
                                }
                              </div>
                              <div className={cx('mox-tech')}>
                                {
                                  massageSettingsOptions?.[0].controlItemConfigs
                                    ?.find(item2 => item2.name === 'moxibustion.tech')
                                    ?.values?.find(item3 => +item3.value === item['moxibustion.tech'])?.label
                                }
                              </div>
                              <div className={cx('duration')}>{item['moxibustion.duration']}分钟</div>
                              <Icon
                                name="close"
                                className={cx('icon')}
                                onClick={() => {
                                  setMoxConfigList(prevValue => prevValue.toSpliced(index, 1));
                                }}
                              />
                            </div>
                          )
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  {new Array(5).fill('').map((_, outerIndex) => (
                    <div key={nanoid(5)} className={cx('acupoint-item', `acupoint-item-${outerIndex + 1}`)}>
                      {moxConfigList.map(
                        (item, index) =>
                          +item['moxibustion.acupoint_area'] === outerIndex + 1 && (
                            <div
                              key={nanoid()}
                              className={cx('config-item', { current: currentSelectConfigIndex === index })}
                              onClick={() => setCurrentSelectConfigIndex(index)}
                            >
                              <div className={cx('index')}>
                                {sortMoxConfigListByAcupoint?.findIndex(item2 => item2.addId === item.addId) + 1}.
                              </div>
                              <div className={cx('acu-area')}>
                                {
                                  massageSettingsOptions?.[0].controlItemConfigs
                                    ?.find(item2 => item2.name === 'moxibustion.acupoint_area')
                                    ?.values?.find(item3 => +item3.value === item['moxibustion.acupoint_area'])?.label
                                }
                              </div>
                              <div className={cx('mox-tech')}>
                                {
                                  massageSettingsOptions?.[0].controlItemConfigs
                                    ?.find(item2 => item2.name === 'moxibustion.tech')
                                    ?.values?.find(item3 => +item3.value === item['moxibustion.tech'])?.label
                                }
                              </div>
                              <div className={cx('duration')}>{item['moxibustion.duration']}分钟</div>
                              <Icon
                                name="close"
                                className={cx('icon')}
                                onClick={() => {
                                  setMoxConfigList(prevValue => prevValue.toSpliced(index, 1));
                                }}
                              />
                            </div>
                          )
                      )}
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
          <div className={cx('plan-info')}>
            <div className={cx('plan-info-left')}>
              <div className={cx('info-item')}>
                <div className={cx('label')}>功效介绍：</div>
                <div className={cx('value')}>{productInfo?.effectIntro}</div>
              </div>
              <div className={cx('info-item', 'item-middle')}>
                <div className={cx('label')}>适合人群：</div>
                <div className={cx('value')}>{productInfo?.targetGroup}</div>
              </div>
              <div className={cx('plan-info-left-footer')}>
                <div className={cx('footer-info-item')}>
                  <div className={cx('label')}>创建时间：</div>
                  <div className={cx('value')}>{currentServingOrder?.createTime}</div>
                </div>
                <div className={cx('footer-info-item')}>
                  <div className={cx('label')}>机器人：</div>
                  <div className={cx('value')}>{currentServingOrder?.deviceId}</div>
                </div>
                {/* 纯人工理疗不展示部位 */}
                {currentServingOrder?.bodyPart !== 'All' && (
                  <div className={cx('footer-info-item')}>
                    <div className={cx('label')}>部位：</div>
                    <div className={cx('value')}>{BODY_PART[currentServingOrder?.bodyPart]}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className={cx('right')}>
          <div className={cx('setting-box')}>
            {currentAllowMaxMinutes >= 10 && !!massageSettingsOptions?.length && (
              <div className={cx('add-area', { disabled: !isRenderModelOK })} onClick={handleAddMatrixMoxArea}>
                <Icon name="add" className={cx('icon')} />
                <span>添加至您的艾灸穴区</span>
              </div>
            )}
            {!!moxConfigList?.[currentSelectConfigIndex] && (
              <div className={cx('mox-setting-wrapper')}>
                {massageSettingsOptions?.[0].controlItemConfigs?.map(item => (
                  <div className={cx('setting-item')} key={item.name}>
                    <div className={cx('label')}>{controlItemName2Label[item.name]?.label}</div>
                    <div className={cx('adjuster', { slider: item.name === 'moxibustion.tech' })}>
                      {item.name === 'moxibustion.tech' ? (
                        <SlideSelect
                          activeColor="white"
                          steps={item.values}
                          value={moxConfigList?.[currentSelectConfigIndex][item.name]}
                          onChange={value =>
                            setMoxConfigList(preValue => {
                              const clonedValue = klona(preValue);
                              clonedValue[currentSelectConfigIndex][item.name] = +value;
                              return clonedValue;
                            })
                          }
                        />
                      ) : (
                        <Stepper
                          {...genMinMaxSteps(item.values)}
                          {...(item.name === 'moxibustion.duration' ? { max: currentMaxDuration } : {})}
                          value={
                            item.name === 'robot.height'
                              ? robotHeight
                              : moxConfigList?.[currentSelectConfigIndex][item.name]
                          }
                          suffix={controlItemName2Label[item.name]?.suffix}
                          formatText={value => {
                            if (item.name === 'robot.height') {
                              return new Decimal(value).div(10).toNumber();
                            } else {
                              return item.values?.find(item2 => +item2.value === value)?.label || value;
                            }
                          }}
                          propertyName={item.name}
                          onChange={value => {
                            if (item.name === 'robot.height') {
                              setRobotHeight(value);
                            } else {
                              setMoxConfigList(preValue => {
                                const clonedValue = klona(preValue);
                                clonedValue[currentSelectConfigIndex][item.name] = value;
                                return clonedValue;
                              });
                            }
                          }}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className={cx('btns')}>
            {currentServingOrder?.stepIsBackable && <Button className={cx('pre-step')}>上一步</Button>}
            <Button
              color="primary"
              disabled={!moxConfigList?.length}
              loading={isConfirmingPlan}
              className={cx('confirm-btn')}
              onClick={handleCheckArm}
            >
              确认并开始
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MassageSetting;
